import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useWBL } from '../../context/WBLContext';
import { formatPhoneNumber } from '../../utils/phone';
import {
  Building2,
  CheckCircle2,
  Users,
  Mail,
  Phone,
  Briefcase,
  Lock,
  ArrowRight,
  AlertCircle,
  Loader2,
  ShieldCheck,
  LogOut,
  Eye,
  EyeOff,
  Home
} from 'lucide-react';
import { auth, db, isUsingMockKeys } from '../../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { UserProfile, OrganizationTeamMember } from '../../types';
import { GlobalFooter } from '../Common/GlobalFooter';

export const OrgTeamOnboardingPage: React.FC = () => {
  const {
    activeUser,
    users,
    tenants,
    activeTenant,
    changeTenant,
    signOutUser,
    setCurrentView,
    updateUserProfile
  } = useWBL();

  // Invite URL Parameters
  const [orgId, setOrgId] = useState('');
  const [orgNameParam, setOrgNameParam] = useState('');
  const [invitedEmail, setInvitedEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [orgRole, setOrgRole] = useState<'admin' | 'supervisor' | 'contact_only'>('supervisor');
  const [regionParam, setRegionParam] = useState('');

  // Resolved primary organization document
  const [primaryOrg, setPrimaryOrg] = useState<UserProfile | null>(null);
  const [isLoadingOrg, setIsLoadingOrg] = useState(true);

  // Auth form state
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authMode, setAuthMode] = useState<'create' | 'signin'>('create');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isConnectedSuccess, setIsConnectedSuccess] = useState(false);

  // Parse query parameters on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const pOrgId = params.get('orgId') || '';
    const pOrgName = params.get('orgName') || '';
    const pEmail = (params.get('email') || '').trim();
    const pName = (params.get('name') || '').trim();
    const pTitle = (params.get('title') || '').trim();
    const pPhone = (params.get('phone') || '').trim();
    const pRole = (params.get('orgRole') || 'supervisor') as 'admin' | 'supervisor' | 'contact_only';
    const pRegion = (params.get('region') || '').trim();

    setOrgId(pOrgId);
    setOrgNameParam(pOrgName);
    setInvitedEmail(pEmail);
    setTitle(pTitle);
    setPhone(formatPhoneNumber(pPhone));
    setOrgRole(pRole);
    setRegionParam(pRegion);

    if (pName) {
      const parts = pName.split(/\s+/);
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' ') || '');
    }

    if (pRegion && pRegion !== 'global') {
      changeTenant(pRegion);
    }
  }, []);

  // Resolve primary organization profile from local state or Firestore
  useEffect(() => {
    let isMounted = true;
    const loadPrimaryOrg = async () => {
      setIsLoadingOrg(true);
      try {
        // 1. Check local context users first
        let found: UserProfile | null = null;
        if (orgId) {
          found = users.find(u => u.uid === orgId) || null;
        }
        if (!found && invitedEmail) {
          const norm = invitedEmail.toLowerCase().trim();
          found =
            users.find(
              u =>
                u.role === 'organization' &&
                !u.parentOrgId &&
                ((u.orgAuthorizedEmails || []).some(e => e.toLowerCase().trim() === norm) ||
                  (u.orgTeamMembers || []).some(m => (m.email || '').toLowerCase().trim() === norm))
            ) || null;
        }

        // 2. Fetch directly from Firestore if not in filtered local users
        if (!found && !isUsingMockKeys) {
          if (orgId) {
            const snap = await getDoc(doc(db, 'users', orgId));
            if (snap.exists()) {
              found = { uid: snap.id, ...(snap.data() as any) } as UserProfile;
            }
          }
          if (!found && invitedEmail) {
            const norm = invitedEmail.toLowerCase().trim();
            const q = query(
              collection(db, 'users'),
              where('orgAuthorizedEmails', 'array-contains', norm)
            );
            const qSnap = await getDocs(q);
            if (!qSnap.empty) {
              const docMatch = qSnap.docs.find(d => !d.data().parentOrgId) || qSnap.docs[0];
              found = { uid: docMatch.id, ...(docMatch.data() as any) } as UserProfile;
            }
          }
        }

        if (isMounted && found) {
          setPrimaryOrg(found);
          const norm = invitedEmail.toLowerCase().trim();
          const memberMatch = (found.orgTeamMembers || []).find(
            m => (m.email || '').toLowerCase().trim() === norm
          );
          if (memberMatch) {
            if (!firstName && !lastName && memberMatch.name) {
              const parts = memberMatch.name.split(/\s+/);
              setFirstName(parts[0] || '');
              setLastName(parts.slice(1).join(' ') || '');
            }
            if (!title && memberMatch.title) setTitle(memberMatch.title);
            if (!phone && memberMatch.phone) setPhone(formatPhoneNumber(memberMatch.phone));
            if (memberMatch.orgRole && memberMatch.orgRole !== 'owner') {
              setOrgRole(memberMatch.orgRole);
            }
          }
        }
      } catch (err) {
        console.warn('OrgTeamOnboarding: Could not load primary organization:', err);
      } finally {
        if (isMounted) setIsLoadingOrg(false);
      }
    };

    if (orgId || invitedEmail) {
      loadPrimaryOrg();
    } else {
      setIsLoadingOrg(false);
    }
    return () => {
      isMounted = false;
    };
  }, [orgId, invitedEmail, users]);

  // Check if an account with this email already exists in local state to default authMode
  useEffect(() => {
    if (!invitedEmail) return;
    const norm = invitedEmail.toLowerCase().trim();
    const exists = users.some(u => (u.email || '').toLowerCase().trim() === norm);
    if (exists) {
      setAuthMode('signin');
    }
  }, [invitedEmail, users]);

  const resolvedOrgName = primaryOrg?.orgName || orgNameParam || 'Partner Organization';
  const resolvedOrgId = primaryOrg?.uid || orgId;
  const resolvedTenantId = primaryOrg?.tenantId || regionParam || activeTenant?.id || 'spartanburg';
  const resolvedRegionName =
    primaryOrg?.region ||
    tenants.find(t => t.id === resolvedTenantId)?.name ||
    activeTenant?.name ||
    'Regional Portal';

  const isLoggedInAsInvitedUser =
    Boolean(activeUser && invitedEmail && activeUser.email?.toLowerCase().trim() === invitedEmail.toLowerCase().trim());
  const isLoggedInAsDifferentUser =
    Boolean(activeUser && invitedEmail && activeUser.email?.toLowerCase().trim() !== invitedEmail.toLowerCase().trim());

  const linkUserToUmbrellaOrg = async (targetUid: string, targetEmail: string) => {
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim() || targetEmail.split('@')[0];
    const initials =
      fullName
        .split(' ')
        .filter(Boolean)
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'OR';

    const existingTeam = primaryOrg?.orgTeamMembers || [];
    const normEmail = targetEmail.toLowerCase().trim();
    const updatedTeam: OrganizationTeamMember[] = existingTeam.some(
      m => (m.email || '').toLowerCase().trim() === normEmail
    )
      ? existingTeam.map(m =>
          (m.email || '').toLowerCase().trim() === normEmail
            ? {
                ...m,
                uid: targetUid,
                name: fullName,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                title: title.trim() || m.title,
                phone: phone.trim() || m.phone
              }
            : m
        )
      : [
          ...existingTeam,
          {
            id: `org-contact-${Date.now()}`,
            uid: targetUid,
            name: fullName,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            title: title.trim() || 'Team Contact',
            email: normEmail,
            phone: phone.trim(),
            orgRole: orgRole === 'contact_only' ? 'supervisor' : orgRole,
            addedAt: new Date().toISOString()
          }
        ];

    const authorizedEmails = Array.from(
      new Set(updatedTeam.map(m => (m.email || '').toLowerCase().trim()).filter(Boolean))
    );

    const memberProfileData: Partial<UserProfile> = {
      uid: targetUid,
      email: normEmail,
      name: fullName,
      contactFirstName: firstName.trim(),
      contactLastName: lastName.trim(),
      professionalTitle: title.trim(),
      contactPhone: phone.trim(),
      role: 'organization',
      parentOrgId: resolvedOrgId || undefined,
      orgRole: orgRole === 'contact_only' ? 'supervisor' : orgRole,
      orgName: resolvedOrgName,
      industry: primaryOrg?.industry || '',
      address: primaryOrg?.address || '',
      website: primaryOrg?.website || '',
      avatar: primaryOrg?.avatar || initials,
      companyProfileText: primaryOrg?.companyProfileText || '',
      onboardingText: primaryOrg?.onboardingText || '',
      targetingMap: primaryOrg?.targetingMap || '',
      extendedRegions: primaryOrg?.extendedRegions || [],
      tenantId: resolvedTenantId,
      region: resolvedRegionName,
      orgTeamMembers: updatedTeam,
      orgAuthorizedEmails: authorizedEmails,
      profileComplete: true,
      firstLogin: false
    };

    if (!isUsingMockKeys) {
      await setDoc(doc(db, 'users', targetUid), memberProfileData, { merge: true });
      if (resolvedOrgId && resolvedOrgId !== targetUid) {
        await setDoc(
          doc(db, 'users', resolvedOrgId),
          {
            orgTeamMembers: updatedTeam,
            orgAuthorizedEmails: authorizedEmails
          },
          { merge: true }
        ).catch(() => {});
      }
    }

    await updateUserProfile(targetUid, memberProfileData);
  };

  const handleConnectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const cleanEmail = invitedEmail.trim().toLowerCase();
    if (!cleanEmail) {
      setErrorMsg('Please enter your invited email address.');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setErrorMsg('Please enter your first and last name.');
      return;
    }

    setIsSubmitting(true);
    try {
      // If already authenticated as the invited user, simply link and proceed
      if (isLoggedInAsInvitedUser && activeUser) {
        await linkUserToUmbrellaOrg(activeUser.uid, cleanEmail);
        setIsConnectedSuccess(true);
        setTimeout(() => {
          if (typeof (window as any).smoothNavigate === 'function') {
            (window as any).smoothNavigate('/');
          } else {
            window.location.href = '/';
          }
          setCurrentView('dashboard');
        }, 1200);
        return;
      }

      if (!password || password.length < 6) {
        setErrorMsg('Please enter a password of at least 6 characters.');
        setIsSubmitting(false);
        return;
      }

      localStorage.setItem('pendingRole', 'organization');

      let uid = '';
      if (authMode === 'create') {
        try {
          const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
          uid = cred.user.uid;
        } catch (createErr: any) {
          if (createErr?.code === 'auth/email-already-in-use') {
            // Automatically fall back to signing in with the provided password
            try {
              const signInCred = await signInWithEmailAndPassword(auth, cleanEmail, password);
              uid = signInCred.user.uid;
            } catch (signInErr: any) {
              setAuthMode('signin');
              throw new Error(
                'An account with this email already exists. Please enter your existing portal password to sign in and connect to ' +
                  resolvedOrgName +
                  '.'
              );
            }
          } else {
            throw createErr;
          }
        }
      } else {
        try {
          const signInCred = await signInWithEmailAndPassword(auth, cleanEmail, password);
          uid = signInCred.user.uid;
        } catch (signInErr: any) {
          if (signInErr?.code === 'auth/user-not-found' || signInErr?.code === 'auth/invalid-credential') {
            // Try creating the account if they haven't registered yet
            try {
              const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
              uid = cred.user.uid;
            } catch {
              throw new Error('Invalid email or password. If you already have an account, please enter your current password.');
            }
          } else {
            throw signInErr;
          }
        }
      }

      if (uid) {
        await linkUserToUmbrellaOrg(uid, cleanEmail);
        setIsConnectedSuccess(true);
        setTimeout(() => {
          if (typeof (window as any).smoothNavigate === 'function') {
            (window as any).smoothNavigate('/');
          } else {
            window.location.href = '/';
          }
          setCurrentView('dashboard');
        }, 1200);
      }
    } catch (err: any) {
      console.error('OrgTeamOnboarding error:', err);
      setErrorMsg(err?.message || 'Unable to complete organization connection. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleConnect = async () => {
    setErrorMsg(null);
    setIsSubmitting(true);
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      if (cred.user) {
        await linkUserToUmbrellaOrg(cred.user.uid, cred.user.email || invitedEmail);
        setIsConnectedSuccess(true);
        setTimeout(() => {
          if (typeof (window as any).smoothNavigate === 'function') {
            (window as any).smoothNavigate('/');
          } else {
            window.location.href = '/';
          }
          setCurrentView('dashboard');
        }, 1200);
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'Google sign-in was cancelled or failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleBadgeLabel =
    orgRole === 'admin'
      ? 'Organization Admin (Full Access)'
      : 'Worksite Supervisor / Mentor';

  return (
    <div className="min-h-screen bg-[#020d1a] text-white flex flex-col justify-between font-sans">
      {/* Top Bar */}
      <header className="w-full border-b border-slate-800/80 bg-[#041527]/90 backdrop-blur-md px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-xl bg-[#0094c8] flex items-center justify-center font-black text-white text-sm shadow-md">
            WBL
          </div>
          <div>
            <span className="text-xs font-black uppercase tracking-widest text-white block">
              Work-Based Learning Portal
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#b9e612] block">
              Organization Team Onboarding • {resolvedRegionName}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            if (typeof (window as any).smoothNavigate === 'function') {
              (window as any).smoothNavigate('/');
            } else {
              window.location.href = '/';
            }
          }}
          className="px-3.5 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-200 text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all cursor-pointer"
        >
          <Home className="w-3.5 h-3.5 text-[#0094c8]" />
          <span>Return to Portal Home</span>
        </button>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-xl bg-white text-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-200"
        >
          {/* Banner Header */}
          <div className="bg-[#011d3c] p-6 md:p-8 text-white relative overflow-hidden">
            <div className="relative z-10 flex items-start sm:items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden shrink-0 shadow-lg">
                {primaryOrg?.avatar && primaryOrg.avatar.startsWith('http') ? (
                  <img src={primaryOrg.avatar} alt={resolvedOrgName} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-8 h-8 text-[#b9e612]" />
                )}
              </div>
              <div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#0094c8]/30 border border-[#0094c8]/50 text-[#b9e612] text-[10px] font-black uppercase tracking-widest mb-2">
                  <Users className="w-3 h-3" />
                  Team Access Invitation
                </span>
                <h1 className="text-xl md:text-2xl font-black tracking-tight text-white uppercase">
                  {resolvedOrgName}
                </h1>
                <p className="text-xs text-slate-300 mt-0.5">
                  {primaryOrg?.industry ? `${primaryOrg.industry} • ` : ''}
                  {resolvedRegionName}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8 space-y-6">
            {isLoadingOrg ? (
              <div className="py-12 flex flex-col items-center justify-center gap-3 text-slate-500">
                <Loader2 className="w-7 h-7 animate-spin text-[#0094c8]" />
                <p className="text-xs font-bold uppercase tracking-wider">Loading Organization Invitation...</p>
              </div>
            ) : isConnectedSuccess ? (
              <div className="py-10 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-9 h-9" />
                </div>
                <h2 className="text-xl font-black text-[#011d3c] uppercase">
                  Connected to {resolvedOrgName}!
                </h2>
                <p className="text-xs text-slate-600 max-w-md mx-auto leading-relaxed">
                  Your account (<strong>{invitedEmail}</strong>) is now linked under{' '}
                  <strong>{resolvedOrgName}</strong> as a <strong>{roleBadgeLabel}</strong>. Redirecting you to your organization dashboard...
                </p>
              </div>
            ) : (
              <>
                {/* Notice if currently signed in as a different user (e.g., primary org owner testing the invite link) */}
                {isLoggedInAsDifferentUser && activeUser && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl space-y-3">
                    <div className="flex items-start gap-2.5">
                      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-xs text-amber-900 leading-relaxed">
                        <p className="font-black uppercase tracking-wider text-amber-800">
                          Currently Signed In As Another Account
                        </p>
                        <p className="mt-1">
                          You are currently signed in as <strong>{activeUser.name || activeUser.email}</strong> (
                          <span className="font-mono">{activeUser.email}</span>), while this invitation is for{' '}
                          <strong>{invitedEmail}</strong>.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        await signOutUser();
                      }}
                      className="w-full py-2.5 px-4 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all cursor-pointer"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      <span>Sign Out of {activeUser.email} &amp; Continue as {invitedEmail}</span>
                    </button>
                  </div>
                )}

                {/* Role & Umbrella Info Box */}
                <div className="p-4 bg-sky-50/70 border border-sky-200/80 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">
                      Assigned Organization Role
                    </span>
                    <span className="text-xs font-black text-[#011d3c] flex items-center gap-1.5 mt-0.5">
                      <ShieldCheck className="w-4 h-4 text-[#0094c8]" />
                      {roleBadgeLabel}
                    </span>
                  </div>
                  {primaryOrg?.name && (
                    <div className="sm:text-right">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">
                        Invited By
                      </span>
                      <span className="text-xs font-bold text-slate-700">{primaryOrg.name}</span>
                    </div>
                  )}
                </div>

                {errorMsg && (
                  <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs font-semibold flex items-start gap-2.5">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-rose-600" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <form onSubmit={handleConnectSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        placeholder="First Name"
                        className="w-full px-3.5 py-2.5 bg-[#f0f4f8] focus:bg-white border border-transparent focus:border-[#0094c8] rounded-xl font-bold text-[#011d3c] text-xs outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        placeholder="Last Name"
                        className="w-full px-3.5 py-2.5 bg-[#f0f4f8] focus:bg-white border border-transparent focus:border-[#0094c8] rounded-xl font-bold text-[#011d3c] text-xs outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                        Title / Position *
                      </label>
                      <input
                        type="text"
                        required
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="e.g. HR Manager, Worksite Supervisor"
                        className="w-full px-3.5 py-2.5 bg-[#f0f4f8] focus:bg-white border border-transparent focus:border-[#0094c8] rounded-xl font-bold text-[#011d3c] text-xs outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        value={phone}
                        onChange={e => setPhone(formatPhoneNumber(e.target.value))}
                        placeholder="(555) 123-4567"
                        className="w-full px-3.5 py-2.5 bg-[#f0f4f8] focus:bg-white border border-transparent focus:border-[#0094c8] rounded-xl font-bold text-[#011d3c] text-xs outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                      Invited Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="email"
                        required
                        value={invitedEmail}
                        onChange={e => setInvitedEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl font-bold text-slate-700 text-xs outline-none"
                      />
                    </div>
                  </div>

                  {!isLoggedInAsInvitedUser && (
                    <div className="pt-2 space-y-3 border-t border-slate-100">
                      <div className="flex items-center justify-between">
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                          {authMode === 'create' ? 'Create Portal Password *' : 'Enter Your Portal Password *'}
                        </label>
                        <button
                          type="button"
                          onClick={() => setAuthMode(prev => (prev === 'create' ? 'signin' : 'create'))}
                          className="text-[10px] font-black text-[#0094c8] hover:underline uppercase tracking-wider cursor-pointer"
                        >
                          {authMode === 'create' ? 'Already have a password? Sign In' : 'New user? Create Password'}
                        </button>
                      </div>

                      <div className="relative">
                        <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          required
                          minLength={6}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder={
                            authMode === 'create'
                              ? 'Create a password (min. 6 characters)'
                              : 'Enter your existing password'
                          }
                          className="w-full pl-10 pr-10 py-3 bg-[#f0f4f8] focus:bg-white border border-transparent focus:border-[#0094c8] rounded-xl font-bold text-[#011d3c] text-xs outline-none transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || isLoggedInAsDifferentUser}
                    className="w-full py-3.5 px-6 bg-[#0094c8] hover:bg-[#007ba6] disabled:opacity-50 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Connecting to {resolvedOrgName}...</span>
                      </>
                    ) : (
                      <>
                        <span>Accept Invitation &amp; Connect to {resolvedOrgName}</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  {!isLoggedInAsInvitedUser && !isLoggedInAsDifferentUser && (
                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={handleGoogleConnect}
                        disabled={isSubmitting}
                        className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-all cursor-pointer"
                      >
                        <span>Or Connect with Google ({invitedEmail || 'Google SSO'})</span>
                      </button>
                    </div>
                  )}
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>

      <GlobalFooter />
    </div>
  );
};
